import { QueryCache, QueryClient } from "@tanstack/react-query"
import { notify } from "shared/ui/Notifications/notify"

/**
 * Thrown by queryFns after a "sequence error" (a business error inside a 200
 * response) has already been surfaced to the user via `handleSequenceError`.
 * The global QueryCache onError skips these to avoid double notifications,
 * while react-query still records the query as errored.
 */
export class HandledApiError extends Error {
  readonly handled = true

  constructor(message: string) {
    super(message)
    this.name = "HandledApiError"
  }
}

/**
 * App-wide QueryClient.
 *
 * - Query errors surface as an ErrorNotification using the query's
 *   `meta.errorTitle` (v5 removed per-query onError). Mutations handle their
 *   own notifications in onSuccess/onError.
 * - refetchOnWindowFocus is off: the app never background-refetched before
 *   the react-query migration; keep request patterns predictable while
 *   domains are converted. Revisit per-query once migration settles.
 */
export const createQueryClient = (): QueryClient =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error instanceof HandledApiError) return
        // Polling queries opt out of toasts (a flaky connection would spam
        // one per interval); their consumers surface state via the query.
        if (query.meta?.silent) return
        const title =
          typeof query.meta?.errorTitle === "string"
            ? query.meta.errorTitle
            : "Request Failed!"
        const message =
          error instanceof Error ? error.message : "Unknown error occurred"
        notify.error(title, message)
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
    },
  })
